import type { DeviceCheckInPerson, DeviceCheckoutView, DeviceFound, DevicePayResult, DeviceState, EventPageView, FormSubmitInput, FormsPacket, PaymentSession, ResolvedAddress } from "@vaulted/shared";
import { formatCents } from "@vaulted/shared";
import { StripeCheckout } from "../components/StripeCheckout";
import { CheckoutBills } from "../components/CheckoutBills";
import NoSleep from "nosleep.js";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { get } from "../api";
import { EventShell } from "./EventBooking";
import { FormsFill } from "../components/FormsFill";
import { Alert, Button, DateOfBirthField, Field } from "../components/ui";
import { WalkIn, WalkInCard, type WalkInStart, useIdle } from "./DeviceWalkIn";
import { Frame, visitLine } from "./Forms";
import { LobbyBook, LobbyGuard, LobbyWelcome, isLobby, rememberLobby, wipe } from "./DeviceLobby";

const KEY = "vaulted-device";
const readToken = () => {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
};
const writeToken = (t: string | null) => {
  try {
    if (t) localStorage.setItem(KEY, t);
    else localStorage.removeItem(KEY);
  } catch {
    /* private mode: it stays paired until the page reloads */
  }
};

class DeviceError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

/** Requests from the iPad carry its own token, not a staff session. */
async function call<T>(token: string | null, method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "omit",
    headers: { ...(token ? { "x-vaulted-device": token } : {}), ...(body !== undefined ? { "Content-Type": "application/json" } : {}), ...(method !== "GET" ? { "x-vaulted-csrf": "1" } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw new DeviceError(res.status, data?.error?.code ?? "error", data?.error?.message ?? "Something went wrong.");
  return data as T;
}

/**
 * A clinic iPad (the check-in stand, the patient iPad): paired once with a code from Settings →
 * Devices. Its home screen is self check-in (last name and date of birth), then each person's missing
 * forms and health screening; the front desk can also send a visit's forms to it.
 */
/**
 * The iPad page, kept awake: iPads only let a page keep the screen on after a tap, so the first tap
 * (on "Tap to start") turns it on (wake lock, or a silent looping video on older iPadOS). The screen
 * locking or the page being hidden drops it, so "Tap to start" comes back until it's tapped again.
 */
export function DevicePage() {
  // The lobby display runs in a kiosk app that keeps the screen on: no "Tap to start".
  const [awake, setAwake] = useState(isLobby);
  const noSleep = useRef<NoSleep | null>(null);
  useEffect(() => {
    noSleep.current = new NoSleep();
    const back = () => {
      // (Not the lobby display: the kiosk app keeps its screen on.)
      if (document.visibilityState === "visible" && !noSleep.current?.isEnabled && !isLobby()) setAwake(false);
    };
    document.addEventListener("visibilitychange", back);
    return () => {
      document.removeEventListener("visibilitychange", back);
      noSleep.current?.disable();
    };
  }, []);
  const start = () => {
    setAwake(true);
    // Must run inside the tap itself; a browser that won't keep the screen on just carries on.
    noSleep.current?.enable().catch(() => undefined);
  };
  return (
    <>
      <DeviceScreens onLobby={() => setAwake(true)} />
      {!awake && (
        <button type="button" onClick={start} className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-2 bg-ink/80 text-white">
          <span className="font-display text-4xl">Tap to start</span>
          <span className="text-base opacity-80">Keeps this iPad's screen on</span>
        </button>
      )}
    </>
  );
}

function DeviceScreens({ onLobby }: { onLobby: () => void }) {
  const { slug = "" } = useParams();
  const [token, setToken] = useState<string | null>(readToken);
  const [state, setState] = useState<DeviceState | null>(null);
  const [offline, setOffline] = useState(false);
  const [finished, setFinished] = useState<string | null>(null);
  const [checkingIn, setCheckingIn] = useState(false);
  const [walkingIn, setWalkingIn] = useState<WalkInStart | null>(null);
  // The lobby's booking (or a special's page) open in its frame.
  const [booking, setBooking] = useState<string | null>(null);
  const api = <T,>(method: string, path: string, body?: unknown) => call<T>(token, method, path, body);

  const refresh = async () => {
    try {
      const s = await call<DeviceState>(token, "GET", "/api/device/state");
      rememberLobby(s.lobby);
      if (s.lobby) onLobby();
      setState(s);
      setOffline(false);
    } catch (e) {
      if (e instanceof DeviceError && e.status === 401) (writeToken(null), setToken(null), setState(null));
      else setOffline(true);
    }
  };
  useEffect(() => {
    if (!token) return;
    let stop = false;
    const tick = () => void (stop ? undefined : refresh());
    tick();
    const t = setInterval(tick, 2500);
    return () => ((stop = true), clearInterval(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // One person's forms finished: back to the check-in list (or the welcome screen).
  const done = async () => {
    setFinished(null);
    await call(token, "POST", "/api/device/done").catch(() => undefined);
    setState((s) => (s ? { ...s, job: null } : s));
    void refresh();
  };
  // Everyone's done (or they walked away): the welcome screen.
  const finish = async () => {
    setFinished(null);
    setCheckingIn(false);
    setWalkingIn(null);
    await call(token, "POST", "/api/device/finish").catch(() => undefined);
    setState((s) => (s ? { ...s, job: null, checkIn: null } : s));
  };
  useEffect(() => {
    if (!finished) return;
    const t = setTimeout(() => void done(), 20_000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  // The lobby display: leaving (or a minute untouched) wipes everything, a walk-in being made included.
  const lobbyFinish = async () => {
    if (state?.walkIn) await call(token, "POST", "/api/device/walk-in/cancel").catch(() => undefined);
    await call(token, "POST", "/api/device/finish").catch(() => undefined);
  };
  const guarded = (el: React.ReactNode) =>
    state?.lobby ? (
      <>
        {el}
        <LobbyGuard slug={slug} finish={lobbyFinish} />
      </>
    ) : (
      el
    );

  if (!token) return <Pair slug={slug} onPaired={(t) => (writeToken(t), setToken(t))} />;
  // A checkout from the front desk comes first.
  if (state?.checkout) return <CheckoutOnIpad token={token} clinicName={state.clinicName} onDone={() => (setState((s) => (s ? { ...s, checkout: false } : s)), void refresh())} footerName={state.name} />;
  if (!state) return <Frame clinic="">{offline ? <Alert>Can't reach the clinic system. Check the Wi-Fi.</Alert> : <p className="py-20 text-center text-muted">Connecting…</p>}</Frame>;
  const footer = (
    <p className="fixed bottom-3 right-4 text-xs text-muted">
      {state.name}
      {offline ? " · offline" : ""}
    </p>
  );

  const job = state.job;
  if (job && finished !== job.appointmentId) {
    const p = job.packet;
    // Back to the check-in list (a self check-in) or the welcome screen.
    const moveOn = () => void (state.checkIn ? done() : finish());
    return guarded(
      <Frame clinic={state.clinicName} onStartOver={() => void finish()}>
        {/* Left alone (or gone to the video call in another tab): back to the welcome screen. */}
        <Idle ms={5 * 60_000} onIdle={() => void finish()} />
        <div className="mb-6">
          <h1 className="font-display text-2xl">Welcome, {p.client.firstName}</h1>
          {visitLine(p) && <p className="text-ink-soft">{visitLine(p)}</p>}
          {p.client.minor && <p className="mt-1 text-sm font-semibold text-wait">A parent or guardian fills in and signs for {p.client.firstName}.</p>}
        </div>
        <FormsFill
          key={`${job.appointmentId}-${job.sentAt}`}
          packet={p}
          onSubmit={async (v: FormSubmitInput) => {
            const next = await call<FormsPacket>(token, "POST", "/api/device/forms", v);
            // All signed: back to the check-in list (a self check-in), or the thank-you screen.
            if (!next.needed.length && !next.screening) {
              if (state.checkIn) void done();
              else setFinished(job.appointmentId);
            }
            return next;
          }}
          doneNote={state.checkIn ? "Thank you." : "Thank you. Please let our team know you're done."}
          screening={{
            // Qualiphy's video visit opens in its own tab, so this screen stays put. The tab is opened
            // during the tap itself (Safari blocks it otherwise), then pointed at the visit.
            start: async () => {
              const tab = window.open("", "_blank");
              try {
                const r = await call<{ url: string | null }>(token, "POST", "/api/device/screening", {});
                if (r.url && tab) tab.location.href = r.url;
                else if (r.url) window.location.href = r.url;
                else tab?.close();
              } catch (e) {
                tab?.close();
                throw e;
              }
              return null;
            },
            later: async () => (await call<{ ending: string | null }>(token, "POST", "/api/device/screening", { later: true })).ending,
            sendOnly: !state.screenings,
            onSent: moveOn,
          }}
          addressLookup={{
            suggest: (q) => call<{ id: string; label: string }[]>(token, "POST", "/api/device/address/suggest", { q }),
            resolve: (id) => call<ResolvedAddress>(token, "POST", "/api/device/address/resolve", { id }),
          }}
        />
        {!p.needed.length && (
          <div className="mt-6 text-center">
            <Button variant="secondary" onClick={() => void done()}>
              {state.checkIn ? "Back" : "Done"}
            </Button>
          </div>
        )}
        {footer}
      </Frame>
    );
  }

  if (finished && !state.checkIn) {
    return guarded(
      <Frame clinic={state.clinicName} onStartOver={() => void finish()}>
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <div className="grid size-16 place-items-center rounded-full bg-ok-wash text-3xl text-ok">✓</div>
          <h1 className="font-display text-3xl">Thank you!</h1>
          <p className="max-w-md text-lg text-ink-soft">You're all set. Please let our team know you're done.</p>
          <Button variant="secondary" onClick={() => void done()}>
            Done
          </Button>
        </div>
        {footer}
      </Frame>
    );
  }

  if (state.checkIn) return guarded(<CheckedIn token={token} state={state} people={state.checkIn.people} onTake={refresh} onFinish={finish} footer={footer} />);
  if (state.walkIn) {
    const cancel = async () => {
      await call(token, "POST", "/api/device/walk-in/cancel").catch(() => undefined);
      setWalkingIn(null);
      void refresh();
    };
    // At the card step, "see the front desk" keeps the visit (flagged: no card); walking away lets it go.
    const frontDesk = async () => {
      await call(token, "POST", "/api/device/walk-in/front-desk").catch(() => undefined);
      setWalkingIn(null);
      void refresh();
    };
    return guarded(<WalkInCard api={api} state={state} walkIn={state.walkIn} onCancel={() => void cancel()} onFrontDesk={() => void frontDesk()} footer={footer} />);
  }
  if (walkingIn && state.walkIns) return guarded(<WalkIn api={api} state={state} start={walkingIn} onCancel={() => setWalkingIn(null)} onBooked={() => (setWalkingIn(null), void refresh())} footer={footer} />);
  if (checkingIn)
    return guarded(
      <FindVisit
        token={token}
        state={state}
        onCancel={() => setCheckingIn(false)}
        onCheckedIn={() => (setCheckingIn(false), void refresh())}
        onWalkIn={(start) => (setCheckingIn(false), setWalkingIn(start))}
        footer={footer}
      />
    );

  if (state.lobby) {
    if (booking) return guarded(<LobbyBook path={booking} onStartOver={() => void wipe(slug, lobbyFinish)} />);
    return <LobbyWelcome slug={slug} state={state} onCheckIn={() => setCheckingIn(true)} onWalkIn={() => setWalkingIn({ returning: null, prefill: null })} onBook={(path) => setBooking(path ?? `/book/${slug}`)} footer={footer} />;
  }
  if (state.place.eventSlug)
    return (
      <EventWelcome slug={state.place.eventSlug} walkUps={state.walkIns} startOnly={state.walkIns && !state.awaitingCheckIn} onCheckIn={() => setCheckingIn(true)} onWalkUp={() => setWalkingIn({ returning: null, prefill: null })} footer={footer}>
        <ClinicWelcome state={state} onCheckIn={() => setCheckingIn(true)} footer={footer} />
      </EventWelcome>
    );
  return <ClinicWelcome state={state} onCheckIn={() => setCheckingIn(true)} footer={footer} />;
}

/**
 * An event's iPad: the event's own look (its hero, logo, colors, and fonts), with Check in and, when
 * the event takes walk-ups, Walk up. Falls back to the plain welcome when the event's page can't be read.
 */
function EventWelcome({ slug, walkUps, startOnly, onCheckIn, onWalkUp, footer, children }: { slug: string; walkUps: boolean; startOnly: boolean; onCheckIn: () => void; onWalkUp: () => void; footer: React.ReactNode; children: React.ReactNode }) {
  const page = useQuery({ queryKey: ["event-page", slug], queryFn: () => get<EventPageView>(`/api/e/${slug}`), retry: false, staleTime: 5 * 60_000 });
  if (page.isLoading) return null;
  if (!page.data) return <>{children}</>;
  const big = "w-full rounded-[6px] px-6 py-5 text-xl font-semibold";
  // Nobody booked is still to check in: straight to walk-up, with a quiet way in for anyone who did book.
  if (startOnly)
    return (
      <EventShell e={page.data} headline="">
        <div className="flex flex-col items-center gap-4 py-6">
          <button type="button" onClick={onWalkUp} className={`${big} bg-[var(--ev-button)] text-white`}>
            Start
            <span className="block text-sm font-normal opacity-90">Get the next open time</span>
          </button>
          <button type="button" onClick={onCheckIn} className="text-sm font-semibold underline opacity-80">
            Have an appointment? Check in
          </button>
        </div>
        {footer}
      </EventShell>
    );
  return (
    <EventShell e={page.data} headline="">
      <div className="flex flex-col gap-4 py-6">
        <button type="button" onClick={onCheckIn} className={`${big} bg-[var(--ev-button)] text-white`}>
          Check in
          <span className="block text-sm font-normal opacity-90">I have an appointment</span>
        </button>
        {walkUps && (
          <button type="button" onClick={onWalkUp} className={`${big} border-2 border-[var(--ev-button)] bg-[var(--ev-card)]`}>
            Walk up
            <span className="block text-sm font-normal opacity-80">No appointment: get the next open time</span>
          </button>
        )}
      </div>
      {footer}
    </EventShell>
  );
}

function ClinicWelcome({ state, onCheckIn, footer }: { state: DeviceState; onCheckIn: () => void; footer: React.ReactNode }) {
  return (
    <Frame clinic={state.clinicName}>
      <div className="flex flex-col items-center gap-6 py-20 text-center">
        <h1 className="font-display text-4xl">Welcome{state.place.kind === "event" ? ` to ${state.place.label}` : ` to ${state.clinicName}`}</h1>
        <p className="max-w-md text-lg text-ink-soft">{state.walkIns ? "Here for an appointment, or walking in? Check yourself in." : "Here for an appointment? Check yourself in."}</p>
        <Button variant="primary" className="px-10 py-4 text-lg" onClick={onCheckIn}>
          Check in
        </Button>
      </div>
      {footer}
    </Frame>
  );
}

/**
 * Checkout sent by the front desk: the client's bill, a tip (always offered), then paying with the card
 * on file, the card reader, or a new card (Stripe's page). Back to the welcome screen once paid.
 */
function CheckoutOnIpad({ token, clinicName, onDone, footerName }: { token: string; clinicName: string; onDone: () => void; footerName: string }) {
  const [v, setV] = useState<DeviceCheckoutView | null>(null);
  const [session, setSession] = useState<PaymentSession | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  // Tapping a card with none on file: keep it for next time (ticked unless they untick it).
  const [saveCard, setSaveCard] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const finish = async () => {
    await call(token, "POST", "/api/device/checkout/done").catch(() => undefined);
    onDone();
  };
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const next = await call<DeviceCheckoutView>(token, "GET", "/api/device/checkout");
        if (!stop) setV(next);
      } catch (e) {
        // Taken back by the front desk.
        if (!stop && e instanceof DeviceError && e.status === 409) onDone();
      }
    };
    void tick();
    const t = setInterval(tick, 2500);
    return () => ((stop = true), clearInterval(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  // Paid: thank them, then the welcome screen.
  useEffect(() => {
    if (!v?.paid) return;
    const t = setTimeout(() => void finish(), 15_000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v?.paid]);

  const act = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setBusy(null);
    }
  };
  const tip = (billId: string, tipCents: number) => act(`tip-${billId}`, async () => setV(await call<DeviceCheckoutView>(token, "POST", "/api/device/checkout/tip", { billId, tipCents })));
  const pay = (method: "card_on_file" | "card_present" | "card") =>
    act(method, async () => {
      const r = await call<DevicePayResult>(token, "POST", "/api/device/checkout/pay", { method, saveCard: method === "card_present" && !v?.card && saveCard });
      setV(r);
      if (r.session) setSession(r.session);
      setMessage(r.message);
    });
  // Changed their mind at the reader or the new-card form: stop it and choose another way.
  const cancelReader = () =>
    act("cancel", async () => {
      setV(await call<DevicePayResult>(token, "POST", "/api/device/checkout/cancel", {}));
      setMessage(null);
      setSession(null);
    });
  const footer = <p className="fixed bottom-3 right-4 text-xs text-muted">{footerName}</p>;

  if (!v) return <Frame clinic={clinicName}><p className="py-20 text-center text-muted">Getting your checkout ready…</p>{footer}</Frame>;
  if (v.paid) {
    return (
      <Frame clinic={clinicName}>
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <div className="grid size-16 place-items-center rounded-full bg-ok-wash text-3xl text-ok">✓</div>
          <h1 className="font-display text-3xl">Paid. Thank you!</h1>
          <p className="max-w-md text-lg text-ink-soft">See you next time.</p>
          <Button variant="secondary" onClick={() => void finish()}>
            Done
          </Button>
        </div>
        {footer}
      </Frame>
    );
  }
  const open = v.bills.filter((b) => b.status === "open");
  return (
    <Frame clinic={clinicName}>
      <div className="mx-auto flex max-w-lg flex-col gap-5 py-6">
        <h1 className="font-display text-3xl">Checkout</h1>
        <CheckoutBills bills={open} tipPercents={v.tipPercents} busyBillId={busy?.startsWith("tip-") ? busy.slice(4) : null} onTip={(id, cents) => void tip(id, cents)} />
        {error && <Alert>{error}</Alert>}
        {session ? (
          <div className="flex flex-col gap-2">
            <StripeCheckout session={session} onComplete={() => setSession(null)} />
            <Button variant="quiet" busy={busy === "cancel"} onClick={() => void cancelReader()}>
              Pay another way
            </Button>
          </div>
        ) : v.waiting || message ? (
          <div className="flex flex-col items-center gap-2 rounded-[var(--radius-panel)] bg-mist px-4 py-6 text-center">
            <p className="text-lg font-semibold">{message ?? "Finishing your payment…"}</p>
            <p className="text-sm text-ink-soft">This screen updates by itself.</p>
            {v.waiting && (
              <Button variant="quiet" busy={busy === "cancel"} onClick={() => void cancelReader()}>
                Cancel and pay another way
              </Button>
            )}
          </div>
        ) : (
          v.dueCents === 0 ? (
          <div className="flex flex-col gap-3">
            <p className="text-center text-lg">Your visit is covered. Add a tip above, or you're all set.</p>
            <Button variant="primary" className="py-4 text-lg" busy={busy === "close"} onClick={() => void act("close", async () => setV(await call<DevicePayResult>(token, "POST", "/api/device/checkout/close", {})))}>
              No tip, I'm done
            </Button>
          </div>
          ) : (
          <div className="flex flex-col gap-3">
            <p className="text-center text-lg">
              Due today: <span className="font-semibold tabular-nums">{formatCents(v.dueCents)}</span>
            </p>
            {v.card && (
              <Button variant="primary" className="py-4 text-lg" busy={busy === "card_on_file"} onClick={() => void pay("card_on_file")}>
                Pay with {v.card.brand.toUpperCase()} •••• {v.card.last4}
              </Button>
            )}
            {v.reader && (
              <Button variant={v.card ? "secondary" : "primary"} className="py-4 text-lg" busy={busy === "card_present"} onClick={() => void pay("card_present")}>
                Tap or insert a card
              </Button>
            )}
            {v.reader && !v.card && (
              <label className="flex items-center justify-center gap-2 text-base">
                <input type="checkbox" className="size-5 accent-[var(--color-brand)]" checked={saveCard} onChange={(e) => setSaveCard(e.target.checked)} />
                Save this card for faster checkout
              </label>
            )}
            <Button variant={v.card || v.reader ? "secondary" : "primary"} className="py-4 text-lg" busy={busy === "card"} onClick={() => void pay("card")}>
              Enter a new card
            </Button>
          </div>
          )
        )}
      </div>
      {footer}
    </Frame>
  );
}


/** Last name and date of birth, then whose visit to check in (theirs, and their party's). */
function FindVisit({
  token,
  state,
  onCancel,
  onCheckedIn,
  onWalkIn,
  footer,
}: {
  token: string;
  state: DeviceState;
  onCancel: () => void;
  onCheckedIn: () => void;
  onWalkIn: (start: WalkInStart) => void;
  footer: React.ReactNode;
}) {
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [found, setFound] = useState<DeviceFound["people"] | null>(null);
  const [known, setKnown] = useState<DeviceFound["known"]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useIdle(90_000, onCancel);

  const find = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await call<DeviceFound>(token, "POST", "/api/device/find", { lastName, dob });
      setFound(r.people);
      setKnown(r.known);
      setPicked(r.people.filter((p) => !p.checkedIn).map((p) => p.appointmentId));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };
  const checkIn = async () => {
    setBusy(true);
    setError(null);
    try {
      await call(token, "POST", "/api/device/check-in", { lastName, dob, appointmentIds: picked.length ? picked : found!.filter((p) => p.self).map((p) => p.appointmentId) });
      onCheckedIn();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
      setBusy(false);
    }
  };

  return (
    <Frame clinic={state.clinicName} onStartOver={onCancel}>
      {found === null ? (
        <form
          className="mx-auto flex max-w-md flex-col gap-4 py-10"
          onSubmit={(e) => {
            e.preventDefault();
            void find();
          }}
        >
          <h1 className="font-display text-3xl">Check in</h1>
          <p className="text-ink-soft">Enter the last name and date of birth of the person the appointment is for.</p>
          <Field label="Last name" autoComplete="off" autoCapitalize="words" value={lastName} onChange={(e) => setLastName(e.target.value)} autoFocus />
          <DateOfBirthField value={dob} onChange={setDob} />
          {error && <Alert>{error}</Alert>}
          <div className="flex gap-3">
            <Button type="submit" variant="primary" className="flex-1 py-3 text-lg" busy={busy} disabled={!lastName.trim() || !dob}>
              Find my appointment
            </Button>
            <Button type="button" variant="quiet" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      ) : found.length === 0 && known.length > 0 ? (
        <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-16 text-center">
          <h1 className="font-display text-2xl">{known.length === 1 ? `No appointment today, ${known[0]!.firstName}` : "No appointment today"}</h1>
          <p className="text-ink-soft">{known.length === 1 ? "Would you like to walk in? We'll find the first open time." : "Would you like to walk in? Tap your name."}</p>
          <div className="flex flex-wrap justify-center gap-3">
            {known.map((k) => (
              <Button key={k.clientId} variant="primary" className="px-8 py-3 text-lg" onClick={() => onWalkIn({ returning: { clientId: k.clientId, firstName: k.firstName, lastName, dob }, prefill: null })}>
                {known.length === 1 ? "Walk in" : k.firstName}
              </Button>
            ))}
            <Button variant="quiet" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      ) : found.length === 0 ? (
        <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-16 text-center">
          <h1 className="font-display text-2xl">{state.walkIns ? "We couldn't find you" : "We couldn't find an appointment today"}</h1>
          <p className="text-ink-soft">{state.walkIns ? "Check the spelling and the date of birth. New here? Walk in and we'll find the first open time." : "Check the spelling and the date of birth, or please see our team."}</p>
          <div className="flex flex-wrap justify-center gap-3">
            {state.walkIns && (
              <Button variant="primary" onClick={() => onWalkIn({ returning: null, prefill: { lastName, dob } })}>
                I'm new: walk in
              </Button>
            )}
            <Button variant={state.walkIns ? "secondary" : "primary"} onClick={() => setFound(null)}>
              Try again
            </Button>
            <Button variant="quiet" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="mx-auto flex max-w-md flex-col gap-4 py-10">
          <h1 className="font-display text-3xl">Hi {found.find((p) => p.self)?.firstName ?? found[0]!.firstName}!</h1>
          <p className="text-ink-soft">{found.length > 1 ? "Who's checking in?" : "Here's your appointment today."}</p>
          <ul className="flex flex-col gap-2">
            {found.map((p) => (
              <li key={p.appointmentId}>
                <label className="flex items-center gap-3 rounded-[var(--radius-control)] border border-line bg-white px-4 py-3">
                  {found.length > 1 && !p.checkedIn && (
                    <input type="checkbox" className="size-5" checked={picked.includes(p.appointmentId)} onChange={(e) => setPicked(e.target.checked ? [...picked, p.appointmentId] : picked.filter((x) => x !== p.appointmentId))} />
                  )}
                  <span className="flex-1">
                    <span className="font-semibold">{p.firstName}</span>
                    <span className="block text-sm text-ink-soft">
                      {p.time} · {p.serviceName}
                    </span>
                  </span>
                  {p.checkedIn && <span className="text-sm font-semibold text-ok">Checked in</span>}
                </label>
              </li>
            ))}
          </ul>
          {error && <Alert>{error}</Alert>}
          <div className="flex gap-3">
            <Button variant="primary" className="flex-1 py-3 text-lg" busy={busy} disabled={found.length > 1 && !picked.length && found.every((p) => !p.checkedIn)} onClick={() => void checkIn()}>
              {found.every((p) => p.checkedIn) ? "Continue" : "Check in"}
            </Button>
            <Button variant="quiet" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {footer}
    </Frame>
  );
}

/** Runs the iPad's idle timer while it's on screen. */
function Idle({ ms, onIdle }: { ms: number; onIdle: () => void }) {
  useIdle(ms, onIdle);
  return null;
}

/** Checked in: what's left for each person (forms, the health screening), then done. */
function CheckedIn({ token, state, people, onTake, onFinish, footer }: { token: string; state: DeviceState; people: DeviceCheckInPerson[]; onTake: () => void; onFinish: () => void; footer: React.ReactNode }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  useIdle(3 * 60_000, onFinish);
  const open = async (appointmentId: string) => {
    setBusy(appointmentId);
    setError(null);
    try {
      await call(token, "POST", "/api/device/take", { appointmentId });
      onTake();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setBusy(null);
    }
  };
  // On an iPad that doesn't do telehealth, a screening whose link went to their phone is done here.
  const screeningLeft = (p: DeviceCheckInPerson) => p.screening && !(!state.screenings && p.screeningSent);
  const allDone = people.every((p) => !p.formsNeeded && !screeningLeft(p));
  // Everything's done: back to the welcome screen after a moment.
  useEffect(() => {
    if (!allDone) return;
    const t = setTimeout(onFinish, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDone]);
  const sendScreening = async (appointmentId: string) => {
    setBusy(`gfe-${appointmentId}`);
    setError(null);
    try {
      await call(token, "POST", "/api/device/screening", { later: true, appointmentId });
      onTake();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setBusy(null);
    }
  };
  // "Text me a link": their forms go to their phone; then back to the welcome screen.
  const [texted, setTexted] = useState<string[] | null>(null);
  const textLink = async () => {
    setBusy("text");
    setError(null);
    try {
      setTexted((await call<{ endings: string[] }>(token, "POST", "/api/device/text-forms", {})).endings);
      setTimeout(onFinish, 6000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setBusy(null);
    }
  };
  return (
    <Frame clinic={state.clinicName} onStartOver={onFinish}>
      <div className="mx-auto flex max-w-md flex-col gap-4 py-10">
        <div className="grid size-14 place-items-center rounded-full bg-ok-wash text-2xl text-ok">✓</div>
        <h1 className="font-display text-3xl">You're checked in</h1>
        <p className="text-ink-soft">{allDone ? "Everything's ready. Have a seat and we'll be right with you." : "A few things to finish before your visit:"}</p>
        <ul className="flex flex-col gap-2">
          {people.map((p) => (
            <li key={p.appointmentId} className="flex flex-wrap items-center gap-3 rounded-[var(--radius-control)] border border-line bg-white px-4 py-3">
              <span className="flex-1">
                <span className="font-semibold">{p.firstName}</span>
                <span className="block text-sm text-ink-soft">
                  {p.time} · {p.serviceName}
                </span>
              </span>
              {p.formsNeeded ? (
                <Button variant="primary" busy={busy === p.appointmentId} onClick={() => void open(p.appointmentId)}>
                  {`Fill out ${p.formsNeeded === 1 ? "1 form" : `${p.formsNeeded} forms`}`}
                </Button>
              ) : screeningLeft(p) ? (
                state.screenings ? (
                  <Button variant="primary" busy={busy === p.appointmentId} onClick={() => void open(p.appointmentId)}>
                    Start the video exam
                  </Button>
                ) : (
                  <Button variant="primary" busy={busy === `gfe-${p.appointmentId}`} onClick={() => void sendScreening(p.appointmentId)}>
                    Send my screening link
                  </Button>
                )
              ) : p.screening ? (
                <span className="text-sm font-semibold text-ok">Screening link sent</span>
              ) : (
                <span className="text-sm font-semibold text-ok">All set</span>
              )}
            </li>
          ))}
        </ul>
        {error && <Alert>{error}</Alert>}
        {texted ? (
          <Alert tone="ok">
            Sent to your phone ending {texted.join(" and ")}. Finish your forms there before your visit.
          </Alert>
        ) : (
          !allDone && (
            <Button variant="secondary" className="py-3" busy={busy === "text"} onClick={() => void textLink()}>
              Text me a link to finish on my phone
            </Button>
          )
        )}
        <Button variant={allDone || texted ? "primary" : "quiet"} className="py-3" onClick={onFinish}>
          {allDone || texted ? "Done" : "I'll finish with the front desk"}
        </Button>
      </div>
      {footer}
    </Frame>
  );
}

/** First time on this iPad: type the code from Settings → Devices. */
function Pair({ slug, onPaired }: { slug: string; onPaired: (token: string) => void }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  // A one-time pairing link (set up remotely, e.g. as a kiosk app's start page): pairs on its own, then
  // drops the code from the address so it isn't kept or shown.
  const linkCode = new URLSearchParams(window.location.search).get("pair");
  const tried = useRef(false);
  useEffect(() => {
    if (!linkCode || tried.current) return;
    tried.current = true;
    window.history.replaceState(null, "", window.location.pathname);
    setBusy(true);
    call<{ token: string }>(null, "POST", "/api/devices/pair", { slug, code: linkCode })
      .then((r) => onPaired(r.token))
      .catch((err) => setError(err instanceof Error ? err.message : "Something went wrong."))
      .finally(() => setBusy(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Frame clinic="">
      <form
        className="mx-auto flex max-w-sm flex-col gap-4 py-16"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            const r = await call<{ token: string }>(null, "POST", "/api/devices/pair", { slug, code });
            onPaired(r.token);
          } catch (err) {
            setError(err instanceof Error ? err.message : "Something went wrong.");
          } finally {
            setBusy(false);
          }
        }}
      >
        <h1 className="font-display text-2xl">Set up this iPad</h1>
        <p className="text-ink-soft">In the clinic app on a computer, go to Settings → Devices, and type the 6-digit code shown for this iPad.</p>
        <Field ref={input} label="Code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} autoFocus />
        {error && <Alert>{error}</Alert>}
        <Button type="submit" variant="primary" busy={busy} disabled={code.length !== 6}>
          Set up
        </Button>
      </form>
    </Frame>
  );
}
